const POLOTNO_ANIMATION_TYPES = new Set(["enter", "exit", "loop"]);
const POLOTNO_ANIMATION_NAMES = new Set([
  "fade",
  "move",
  "zoom",
  "rotate",
  "blink",
  "bounce",
  "cameraZoom",
]);

function normalizeAnimations(animations = []) {
  if (!Array.isArray(animations)) {
    return [];
  }

  return animations
    .map((animation) => ({
      type: POLOTNO_ANIMATION_TYPES.has(animation?.type) ? animation.type : "enter",
      name: POLOTNO_ANIMATION_NAMES.has(animation?.name) ? animation.name : "fade",
      delay: Math.max(Number(animation?.delay) || 0, 0),
      duration: Math.max(Number(animation?.duration) || 700, 1),
      enabled: animation?.enabled !== false,
      data: animation?.data && typeof animation.data === "object" ? animation.data : {},
    }))
    .filter((animation) => animation.duration > 0);
}

function sanitizeCustom(value = {}, allowedKeys = []) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return allowedKeys.reduce((result, key) => {
    const item = value[key];
    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
      result[key] = item;
    }
    return result;
  }, {});
}

function sanitizePolotnoElement(element) {
  if (!element || typeof element !== "object" || !element.type) {
    return null;
  }

  const base = {
    id: element.id,
    type: element.type,
    name: element.name || "",
    x: Number(element.x) || 0,
    y: Number(element.y) || 0,
    width: Math.max(Number(element.width) || 1, 1),
    height: Math.max(Number(element.height) || 1, 1),
    rotation: Number(element.rotation) || 0,
    opacity: typeof element.opacity === "number" ? element.opacity : 1,
    visible: element.visible !== false,
    selectable: element.selectable !== false,
    removable: element.removable !== false,
    draggable: element.draggable !== false,
    resizable: element.resizable !== false,
    contentEditable: element.contentEditable !== false,
    styleEditable: element.styleEditable !== false,
    alwaysOnTop: Boolean(element.alwaysOnTop),
    showInExport: element.showInExport !== false,
    custom: sanitizeCustom(element.custom, ["role", "asset_role", "placeholder_key"]),
    animations: normalizeAnimations(element.animations || element.animation),
  };

  if (element.type === "text") {
    return {
      ...base,
      text: element.text || "",
      placeholder: element.placeholder || "",
      fontSize: Number(element.fontSize) || 32,
      fontFamily: element.fontFamily || "Roboto",
      fontStyle: element.fontStyle || "normal",
      fontWeight: element.fontWeight || "normal",
      textDecoration: element.textDecoration || "",
      fill: element.fill || "black",
      align: element.align || "center",
      verticalAlign: element.verticalAlign || "top",
      strokeWidth: Number(element.strokeWidth) || 0,
      stroke: element.stroke || "black",
      lineHeight: element.lineHeight || 1.2,
      letterSpacing: Number(element.letterSpacing) || 0,
      backgroundEnabled: Boolean(element.backgroundEnabled),
      backgroundColor: element.backgroundColor || "#7ED321",
      backgroundOpacity: typeof element.backgroundOpacity === "number" ? element.backgroundOpacity : 1,
      backgroundCornerRadius:
        typeof element.backgroundCornerRadius === "number" ? element.backgroundCornerRadius : 0.5,
      backgroundPadding: typeof element.backgroundPadding === "number" ? element.backgroundPadding : 0.5,
    };
  }

  if (element.type === "figure") {
    return {
      ...base,
      subType: element.subType || "rect",
      fill: element.fill || "rgba(0, 161, 255, 1)",
      dash: Array.isArray(element.dash) ? element.dash.filter((item) => typeof item === "number") : [],
      strokeWidth: Number(element.strokeWidth) || 0,
      stroke: element.stroke || "rgba(98, 197, 255, 1)",
      cornerRadius: Number(element.cornerRadius) || 0,
    };
  }

  if (element.type === "svg") {
    return {
      ...base,
      src: element.src || element.svgSource || "",
      maskSrc: element.maskSrc || "",
      cropX: typeof element.cropX === "number" ? element.cropX : 0,
      cropY: typeof element.cropY === "number" ? element.cropY : 0,
      cropWidth: typeof element.cropWidth === "number" ? element.cropWidth : 1,
      cropHeight: typeof element.cropHeight === "number" ? element.cropHeight : 1,
      keepRatio: element.keepRatio !== false,
      stretchEnabled: Boolean(element.stretchEnabled),
      flipX: Boolean(element.flipX),
      flipY: Boolean(element.flipY),
      borderColor: element.borderColor || "black",
      borderSize: Number(element.borderSize) || 0,
      cornerRadius: Number(element.cornerRadius) || 0,
      colorsReplace: element.colorsReplace || {},
    };
  }

  if (element.type === "image" || element.type === "video") {
    if (!element.src) {
      return {
        ...base,
        type: "figure",
        subType: "rect",
        fill: "rgba(255, 243, 209, 0.28)",
        dash: [],
        strokeWidth: 2,
        stroke: "rgba(212, 175, 55, 0.58)",
        cornerRadius: 22,
      };
    }

    return {
      ...base,
      src: element.src,
      cropX: typeof element.cropX === "number" ? element.cropX : 0,
      cropY: typeof element.cropY === "number" ? element.cropY : 0,
      cropWidth: typeof element.cropWidth === "number" ? element.cropWidth : 1,
      cropHeight: typeof element.cropHeight === "number" ? element.cropHeight : 1,
      keepRatio: element.keepRatio !== false,
      flipX: Boolean(element.flipX),
      flipY: Boolean(element.flipY),
      clipSrc: element.clipSrc || "",
      borderColor: element.borderColor || "rgba(0,0,0,0)",
      borderSize: Number(element.borderSize) || 0,
      cornerRadius: Number(element.cornerRadius) || 0,
    };
  }

  return element;
}

function sanitizePolotnoAudio(audio = {}) {
  const src = audio.src || audio.url || "";
  if (!src) {
    return null;
  }

  return {
    id: audio.id,
    src,
    name: audio.name || "",
    delay: Math.max(Number(audio.delay) || 0, 0),
    volume: typeof audio.volume === "number" ? audio.volume : 1,
    startTime: typeof audio.startTime === "number" ? audio.startTime : 0,
    endTime: typeof audio.endTime === "number" ? audio.endTime : 1,
    duration: Math.max(Number(audio.duration) || 5000, 1),
  };
}

export function clonePolotnoJson(value) {
  if (!value) {
    return null;
  }

  return structuredClone ? structuredClone(value) : JSON.parse(JSON.stringify(value));
}

export function rebuildPolotnoJsonSafely(store, json) {
  const pages = Array.isArray(json?.pages) ? json.pages : [];
  if (!store || !pages.length) {
    return false;
  }

  const storeWidth = Number(json.width) || 1080;
  const storeHeight = Number(json.height) || 1920;

  store.clear();
  store.set({
    width: storeWidth,
    height: storeHeight,
    unit: json.unit || "px",
    dpi: Number(json.dpi) || 72,
    custom: sanitizeCustom(json.custom, ["generation_target", "template_family"]),
  });

  pages.forEach((page) => {
    const newPage = store.addPage({
      id: page.id,
      width: Number(page.width) || storeWidth,
      height: Number(page.height) || storeHeight,
      background: page.background || "white",
      bleed: Number(page.bleed) || 0,
      custom: sanitizeCustom(page.custom, ["scene_id", "scene_name"]),
      duration: Number(page.duration) || 5000,
      children: [],
    });

    (page.children || []).forEach((child) => {
      const sanitizedChild = sanitizePolotnoElement(child);
      if (!sanitizedChild) {
        return;
      }

      try {
        newPage.addElement(sanitizedChild, { skipSelect: true });
      } catch (elementError) {
        console.error("Skipped invalid Polotno element during safe rebuild", {
          id: child.id,
          type: child.type,
          error: elementError,
        });
      }
    });
  });

  (Array.isArray(json.audios) ? json.audios : []).forEach((audio) => {
    const sanitizedAudio = sanitizePolotnoAudio(audio);
    if (!sanitizedAudio || !store.addAudio) {
      return;
    }

    try {
      store.addAudio(sanitizedAudio);
    } catch (audioError) {
      console.error("Skipped invalid Polotno audio during safe rebuild", {
        id: audio.id,
        error: audioError,
      });
    }
  });

  if (store.pages?.[0]?.id && store.selectPage) {
    try {
      store.selectPage(store.pages[0].id);
    } catch (_error) {
      // Non-fatal; Polotno will still show the store's default active page.
    }
  }

  return store.pages.length > 0;
}
